/**
 * useNavigation Hook
 *
 * Manages navigation-related functionality including:
 * - GetNavigation function
 * - Category processing logic
 * - Navigation state management
 * - Category limit handling
 * - Breadcrumb navigation
 *
 * TODO: Extract GetNavigation function from navigation.js
 * TODO: Move category processing and ordering logic
 * TODO: Extract uncategorized asset integration
 * TODO: Create navigation state management functions
 * TODO: Integrate category limit handling
 */

import { useCallback, useRef } from 'react'
import {
  getRequestUserCategories,
  getRequestAssetsByQuery,
  postRequestSignedUrls,
  getAuthToken,
  fetchDocuments
} from '../../../assets/requests'
import placeholderImg from '../../../assets/images/camera-icon-bg2.jpg'
import { useSubscriptionManagement } from './useSubscriptionManagement'

export const useNavigation = () => {
  const isFetchingRef = useRef(false)
  const lastNavFetchAttemptedRef = useRef(false)
  const lastNavFetchSucceededRef = useRef(false)

  const { fetchUserCategoryLimit, applyCategoryLimits } =
    useSubscriptionManagement()

  // Utility function to order events alphabetically
  const orderEvents = useCallback(events => {
    events.sort(function (event1, event2) {
      if (event1.name > event2.name) {
        return 1
      } else if (event1.name < event2.name) {
        return -1
      } else {
        return 0
      }
    })
    return events
  }, [])

  // Utility function to order years numerically
  const orderYears = useCallback(years => {
    return years.sort(function (years1, years2) {
      return years1.name - years2.name
    })
  }, [])

  const getSignedUrls = useCallback(async (assets, userId) => {
    const token = await getAuthToken()
    if (!token) {
      console.warn('Cannot get signed URLs: no authentication token available')
      return assets
    }

    const assetIds = Object.keys(assets)
    if (assetIds.length === 0) return assets

    let thumbnailKeys = []
    let s3Uris = []
    let s3UriIdx = []

    assetIds.forEach((id, idx) => {
      const asset = assets[id]
      if (!asset.thumbnailUrl && asset.thumbnails && asset.thumbnails.length > 0) {
        // TODO: don't assume index 1 is always the thumbnail with the desired size - choose it
        thumbnailKeys.push(asset.thumbnails[1])
      }

      if (!asset.fileUrl) {
        s3Uris.push(asset.s3uri)
        s3UriIdx.push(idx)
      }
    })

    if (thumbnailKeys.length === 0 && s3Uris.length === 0) {
      return assets
    }

    let signedThumbnailUrls = []
    let signedS3Uris = []

    if (thumbnailKeys.length > 0) {
      try {
        signedThumbnailUrls = await postRequestSignedUrls({
          userId,
          payload: thumbnailKeys
        })
      } catch (error) {
        console.warn('Failed to get signed thumbnail URLs:', error)
      }
    }

    const isAuthComplete = token !== null && token !== undefined
    if (!isAuthComplete) {
      console.warn('Authentication not complete. Skipping signed URL requests.')
      return assets
    }

    if (s3Uris.length > 0) {
      try {
        signedS3Uris = await postRequestSignedUrls({
          userId,
          payload: s3Uris
        })
      } catch (error) {
        console.warn('Failed to get signed S3 URLs:', error)
      }
    }

    if (!Array.isArray(signedThumbnailUrls)) {
      signedThumbnailUrls = []
    }
    if (!Array.isArray(signedS3Uris)) {
      signedS3Uris = []
    }

    assetIds.forEach((id, idx) => {
      if (signedThumbnailUrls && signedThumbnailUrls[idx]) {
        assets[id].thumbnailUrl = signedThumbnailUrls[idx]
      }

      const s3UriSignedIndex = s3UriIdx.indexOf(idx)
      if (
        s3UriSignedIndex !== -1 &&
        signedS3Uris &&
        signedS3Uris[s3UriSignedIndex]
      ) {
        assets[id].fileUrl = signedS3Uris[s3UriSignedIndex]
      }
    })

    return assets
  }, [])

  const getCandidateAsset = useCallback(
    async (categoryId, categoryIds, userId, assetType) => {
      try {
        const token = await getAuthToken()
        if (!token) {
          console.error(
            'Cannot get candidate asset: no authentication token available'
          )
          return null
        }

        // Include all categories
        let uniqueCategories = [categoryId, ...categoryIds]
        uniqueCategories = [...new Set(uniqueCategories)]

        const params = { cat: uniqueCategories, page: 1, limit: 10 }
        if (assetType) {
          params.type = assetType
        }

        const response = await getRequestAssetsByQuery({
          userId,
          params
        })

        if (response && Object.keys(response).length > 0) {
          return Object.values(response)[0] || null
        }

        return null
      } catch (error) {
        console.error('Error fetching candidate asset:', error)
        return null
      }
    },
    []
  )

  // Capitalize first letter of string
  const capitalizeFirstLetter = useCallback(string => {
    if (!string) return string // Ensure the input is not null, undefined, or an empty string
    return string.charAt(0).toUpperCase() + string.slice(1)
  }, [])

  // Fetch and sign uncategorized assets
  const fetchAndSignUncategorizedAssets = useCallback(
    async userId => {
      const token = await getAuthToken()
      if (!token) {
        console.error(
          'Cannot fetch uncategorized assets: no authentication token available'
        )
        return {
          id: 'uncategorized',
          name: 'Uncategorized',
          src: placeholderImg,
          assets: []
        }
      }

      const uncategorizedCategory = {
        id: 'uncategorized',
        name: 'Uncategorized',
        src: placeholderImg,
        assets: []
      }

      try {
        const params = { cat: 'none', type: 'image', limit: 25 }
        const uncategorizedAssets = await getRequestAssetsByQuery({
          userId,
          params
        })

        if (
          uncategorizedAssets &&
          Object.keys(uncategorizedAssets).length > 0
        ) {
          const signedAssets = await getSignedUrls(uncategorizedAssets, userId)
          const assetsArray = Object.values(signedAssets)

          uncategorizedCategory.assets = assetsArray

          if (assetsArray.length > 0 && assetsArray[0].thumbnailUrl) {
            uncategorizedCategory.thumbnailUrl = assetsArray[0].thumbnailUrl
          }
        }

        return uncategorizedCategory
      } catch (error) {
        console.error('Error fetching uncategorized assets:', error)
        return uncategorizedCategory
      }
    },
    [getSignedUrls]
  )

  const fetchAndSignDocuments = useCallback(
    async userId => {
      const token = getAuthToken()
      if (!token) {
        console.error(
          'Cannot fetch documents: no authentication token available'
        )
        return []
      }

      try {
        const documents = await fetchDocuments({ userId })

        if (documents && Object.keys(documents).length > 0) {
          const signedDocuments = await getSignedUrls(documents, userId)
          return Object.values(signedDocuments).map(doc => ({
            ...doc,
            type: 'document',
            name: doc.filename || 'Document',
          }))
        }

        return []
      } catch (error) {
        console.error('Error fetching documents:', error)
        // If it's an auth error, don't retry - let the auth system handle it
        if (
          error.message.includes('401') ||
          error.message.includes('Authentication expired')
        ) {
          throw error
        }
        return []
      }
    },
    [getSignedUrls]
  )

  const getNavigation = useCallback(
    async ({ userId, selectedCategories, assetType, dispatch }) => {
      try {
        const token = getAuthToken()
        if (!token) {
          console.error(
            'Cannot get navigation: no authentication token available'
          )
          return []
        }

        // get a list of all categories that include the user's assets
        const initialCategories = await getRequestUserCategories({
          userId: userId,
          assetType: assetType,
          log: 'User Categories'
        })

        if (!initialCategories || initialCategories.length === 0) {
          if (selectedCategories.length < 2) {
            // Only set showAssets true if not in a definitive asset view state
            dispatch({ type: 'set_show_assets', payload: true })
          } else {
            dispatch({ type: 'set_show_assets', payload: false })
          }
          return []
        }

        // Step 1: Fetch candidate assets for all categories in parallel
        const categoryIds = selectedCategories.map(cat => cat.id)
        const candidateResults = await Promise.all(
          initialCategories.map(async category => {
            const candidateAsset = await getCandidateAsset(
              category.id,
              categoryIds,
              userId,
              assetType
            )
            return { category, candidateAsset }
          })
        )

        // Step 2: Collect all thumbnail keys that need signing into a single batch
        const pxlSize = 200
        const thumbnailFilename = `${pxlSize}px.jpeg`
        const keysToSign = []
        const keyToCategoryIndex = []

        candidateResults.forEach(({ candidateAsset }, idx) => {
          if (!candidateAsset) return

          let thumbnailKey = ''
          if (candidateAsset.thumbnails?.length > 0) {
            for (const t of candidateAsset.thumbnails) {
              if (t.endsWith(thumbnailFilename)) {
                thumbnailKey = t
                break
              }
            }
          }
          if (!thumbnailKey) thumbnailKey = candidateAsset.thumbnails?.[1] || ''
          if (!thumbnailKey) return

          keysToSign.push(thumbnailKey)
          keyToCategoryIndex.push(idx)
        })

        // Step 3: Sign all thumbnail URLs in a single batch request
        let signedUrls = []
        if (keysToSign.length > 0) {
          try {
            signedUrls = await postRequestSignedUrls({
              userId,
              payload: keysToSign
            })
            if (!Array.isArray(signedUrls)) signedUrls = []
          } catch (error) {
            console.warn('Failed to batch-sign category thumbnails:', error)
          }
        }

        // Step 4: Build processed categories with signed URLs
        // Filter out categories that have no candidate asset (no assets for that category)
        const processedCategories = candidateResults
          .map(({ category, candidateAsset }, idx) => {
            if (!candidateAsset) return null

            const processedName =
              category.type === 'event' && category.userId
                ? capitalizeFirstLetter(category.name)
                : category.name

            // Find if this category had a signed URL
            const signedIdx = keyToCategoryIndex.indexOf(idx)
            const thumbnailUrl = signedIdx !== -1 ? signedUrls[signedIdx] || null : null

            return {
              ...category,
              thumbnailUrl,
              name: processedName
            }
          })
          .filter(cat => cat !== null)

        // group categories into the rows we want to display them in
        let events = []
        let kinEvents = []
        let years = []

        processedCategories.forEach(category => {
          if (category.type === 'event') {
            if (category.userId) {
              kinEvents.push(category)
            } else {
              events.push(category)
            }
          } else if (category.type === 'year') {
            years.push(category)
          }
        })

        events = orderEvents(events)
        kinEvents = orderEvents(kinEvents)
        years = orderYears(years)

        let sc = selectedCategories
        let results = []
        let selectedCategoryName = sc.length > 0 ? sc[0].name : null

        if (sc.length === 2) {
          // gallery level 3a - don't show any rows of categories
          results = []
        } else if (
          sc.length === 1 &&
          sc[0].type === 'event' &&
          years.length > 0
        ) {
          // gallery level 2 (event category was picked) - show the years for the selected event category
          results = [
            { name: selectedCategoryName || 'Years', subCategories: years }
          ]
        } else if (
          sc.length === 1 &&
          sc[0].type === 'year' &&
          events.length > 0
        ) {
          // gallery level 3b (year category was picked) - don't show any rows of categories
          results = []
        } else if (sc.length === 0) {
          // gallery level 1 - show all 3 group rows if available
          if (events.length > 0) {
            results.push({ name: 'Events', subCategories: events })
          }
          if (kinEvents.length > 0) {
            results.push({ name: 'Kin-Categories', subCategories: kinEvents })
          }
          if (years.length > 0) {
            results.push({ name: 'Years', subCategories: years })
          }
        }

        if (results.length === 0) {
          dispatch({ type: 'set_show_assets', payload: true })
        } else {
          dispatch({ type: 'set_show_assets', payload: false })
        }

        return results
      } catch (error) {
        console.error('Error getting navigation:', error)
        return []
      }
    },
    [
      getCandidateAsset,
      capitalizeFirstLetter,
      orderEvents,
      orderYears
    ]
  )

  // Main navigation data fetching with uncategorized integration
  const fetchNavigationData = useCallback(
    async ({
      userId,
      selectedCategories,
      assetType,
      dispatch,
      setNavigation,
      setDocuments
    }) => {
      if (isFetchingRef.current) {
        return
      }

      // Ensure we have a valid token and userId before proceeding
      const token = getAuthToken()
      if (!token || !userId) {
        console.log(
          'No auth token or userId available, skipping navigation fetch'
        )
        return
      }

      isFetchingRef.current = true
      lastNavFetchAttemptedRef.current = true

      try {
        const isTopLevel = selectedCategories.length === 0

        // Only fetch documents and uncategorized at the top level (no selected categories)
        const [documents, uncategorizedCategory, navigationData_] = await Promise.all([
          isTopLevel ? fetchAndSignDocuments(userId) : Promise.resolve([]),
          isTopLevel ? fetchAndSignUncategorizedAssets(userId) : Promise.resolve({ id: 'uncategorized', name: 'Uncategorized', assets: [] }),
          getNavigation({
            userId,
            selectedCategories,
            assetType,
            dispatch
          })
        ])

        if (setDocuments && isTopLevel) {
          setDocuments(documents)
        }

        let navigationData = navigationData_

        // navigation structure for users with only uncategorized images
        // Only create minimal structure if no categories are selected
        if (
          navigationData.length === 0 &&
          uncategorizedCategory.assets.length > 0 &&
          selectedCategories.length === 0
        ) {
          navigationData = [
            {
              name: 'Years',
              subCategories: [uncategorizedCategory]
            }
          ]
          // uncategorized category in years list view
          dispatch({ type: 'set_show_assets', payload: false })
        }

        // Integrate uncategorized assets into Years category
        const yearsCategoryIndex = navigationData.findIndex(
          category => category.name === 'Years'
        )
        if (yearsCategoryIndex !== -1) {
          const yearsCategory = navigationData[yearsCategoryIndex]
          const isUncategorizedAdded = yearsCategory.subCategories.some(
            subCategory => subCategory.name === `Uncategorized`
          )

          if (!isUncategorizedAdded) {
            yearsCategory.subCategories.push(uncategorizedCategory)
          }
        }

        setNavigation(navigationData)
        lastNavFetchSucceededRef.current = true
      } catch (error) {
        console.error('Error fetching navigation data:', error)
        lastNavFetchSucceededRef.current = false
      } finally {
        isFetchingRef.current = false
      }
    },
    [fetchAndSignUncategorizedAssets, fetchAndSignDocuments, getNavigation]
  )

  // Add uncategorized category to Years if not already present
  const addUncategorizedToYears = useCallback(navigation => {
    return navigation?.map(superElm => {
      if (superElm.name === 'Years') {
        const uncategorized = {
          id: `uncategorized`,
          name: `Uncategorized`,
          src: '',
          assets: []
        }

        const isUncategorizedAdded = superElm.subCategories.some(
          subCategory => subCategory.name === `Uncategorized`
        )

        if (!isUncategorizedAdded) {
          return {
            ...superElm,
            subCategories: [...superElm.subCategories, uncategorized]
          }
        }
      }
      return superElm
    })
  }, [])

  return {
    getNavigation,
    fetchNavigationData,
    fetchUserCategoryLimit,
    fetchAndSignUncategorizedAssets,
    fetchAndSignDocuments,
    applyCategoryLimits,
    addUncategorizedToYears,
    isFetchingRef,
    lastNavFetchAttemptedRef,
    lastNavFetchSucceededRef,
    // Utility functions
    orderEvents,
    orderYears,
    getCandidateAsset,
    getSignedUrls,
    capitalizeFirstLetter
  }
}

export default useNavigation
